import React, { useState } from 'react';
import cx from 'classnames';
import { Options as FocusTrapOptions } from 'focus-trap';

import ChevronDownS2 from '../Icons/ChevronDownS2';
import ChevronUpS2 from '../Icons/ChevronUpS2';
import Text from '../Text';
import Touchable from '../Touchable';
import Popover from '../Popover';
import Button from '../Button';

import { renderLabels } from './helpers';

import '../styles/_utils.scss';
import styles from './FilterItem.module.scss';

export interface FilterItemProps {
  /** Children of `Filter.Item`. */
  children: React.ReactNode;
  /** Accessible name for the  `Filter.Item`, which will be visually hidden by default. */
  label: string;
  /** The visible label which by default will show the `label` prop value. It will then show the filter values when applied. */
  displayLabel: null | string | number | string[];
  /** Sets the `Filter.Item` to a disabled state.  */
  disabled?: boolean;
  /**
   * Options that will be passed into Popover's FocusTrap component.
   *
   * @see https://github.com/focus-trap/focus-trap#createoptions
   *
   * */
  focusTrapOptions?: FocusTrapOptions;
  /** Function that will be called when the reset button is clicked. */
  onReset?: () => void;
  /** Function that will be called when the apply button is clicked. */
  onApply?: () => void;
}

const FilerButtonLabel: React.FC<{
  displayLabel: FilterItemProps['displayLabel'];
  label: FilterItemProps['label'];
  isOpen: boolean;
}> = ({ displayLabel, label, isOpen }) => {
  const { renderedLabel, screenReaderLabel } = renderLabels({
    displayLabel,
    label,
  });

  return (
    <Text
      size={14}
      weight='semiBold'
      className={cx(styles.btnText)}
      as='span'
      contrast='low'
      aria-label={screenReaderLabel}
    >
      {renderedLabel}
      {isOpen ? <ChevronUpS2 /> : <ChevronDownS2 />}
    </Text>
  );
};

export const FilterItem: React.FC<FilterItemProps> = ({
  children,
  label,
  displayLabel,
  disabled = false,
  focusTrapOptions,
  onReset,
  onApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  const isFilterActive =
    (Array.isArray(displayLabel) && displayLabel.length !== 0) ||
    (!Array.isArray(displayLabel) && displayLabel !== null);

  return (
    <>
      <Touchable
        ref={setTargetElement}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className={cx(
          styles.filterBtn,
          isFilterActive && styles.isActive,
          isOpen && styles.isExpanded,
          disabled && styles.isDisabled,
        )}
        disabled={disabled}
        aria-haspopup='dialog'
      >
        <FilerButtonLabel
          displayLabel={displayLabel}
          label={label}
          isOpen={isOpen}
        />
      </Touchable>
      {isOpen && (
        <Popover
          isOpen={isOpen}
          label={label}
          focusTrapOptions={focusTrapOptions}
          targetElement={targetElement}
          setOpen={() => setIsOpen(!isOpen)}
        >
          <Popover.Header>
            <Text appearance='h4' as='h1' size={16} weight='semiBold'>
              {label}
              <span className='visually-hidden'>filter</span>
            </Text>
          </Popover.Header>
          <Popover.Body>{children}</Popover.Body>
          <Popover.Footer>
            <Button
              inline
              centered
              tertiary
              size={32}
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel <span className='visually-hidden'>filter</span>
            </Button>
            <Button
              inline
              centered
              secondary
              size={32}
              onClick={() => {
                onReset !== undefined && onReset();
              }}
            >
              Reset to default <span className='visually-hidden'>filter</span>
            </Button>
            <Button
              inline
              centered
              size={32}
              onClick={() => {
                onApply !== undefined && onApply();
                setIsOpen(false);
              }}
            >
              Apply <span className='visually-hidden'>filter</span>
            </Button>
          </Popover.Footer>
        </Popover>
      )}
    </>
  );
};

export default FilterItem;
export { styles };
